export const ALL_CONTACT_TYPES = [
    'Person',
    'Company',
    'Deceased',
    'PowerOfAttorney',
    'Estate',
] as const;
export type ContactType = (typeof ALL_CONTACT_TYPES)[number];

/**
 * How a matter party (contacts row) got linked to its firm contact:
 * backfill = initial migration, auto = matched on identity_key when created,
 * picked = chosen in the New Prospect modal, manual = moved / merged by staff.
 */
export const ALL_FIRM_CONTACT_LINK_SOURCES = ['backfill', 'auto', 'picked', 'manual'] as const;
export type FirmContactLinkSource = (typeof ALL_FIRM_CONTACT_LINK_SOURCES)[number];

/**
 * When a matter party's ID verification is sent without anyone clicking Send: with the intake
 * email when the matter is created, or once the client submits the intake
 */
export const ALL_IDV_SEND_TIMINGS = ['WithIntake', 'OnCompletion'] as const;
export type IdvSendTiming = (typeof ALL_IDV_SEND_TIMINGS)[number];

export const ALL_APPROVAL_RESPONSE_STATUSES = [
    'Success',
    'LawyerApprovalNotRequired',
    'LawyerApprovalPending',
    'LawyerDeactivated',
    'ProcessNotInitiated',
    'InvalidAction',
    'LawyerAlreadyApproved',
    'ClientAlreadyAccepted',
    'ClientAlreadyDeclined',
    'TimedOut',
    'LawyerAlreadyRejected',
    'ActionMissing',
    'NotFound',
    'NoSuchInstanceFound',
    'NewManualQuoteSent',
    'ClientAlreadyRequestedACall',
    'Expired',
    'Failed',
    'Terminated',
    'WorkflowAlreadyCompleted',
] as const;
export type ApprovalResponseStatus = (typeof ALL_APPROVAL_RESPONSE_STATUSES)[number];

export const APPROVAL_STATUS_TITLES = {
    Success: 'Success',
    LawyerApprovalNotRequired: 'Lawyer Approval Not Required',
    LawyerApprovalPending: 'Lawyer Approval Pending',
    LawyerDeactivated: 'Lawyer Deactivated',
    ProcessNotInitiated: 'Process Not Initiated',
    InvalidAction: 'Invalid Action',
    LawyerAlreadyApproved: 'Action has already been taken',
    ClientAlreadyAccepted: 'Action has already been taken',
    ClientAlreadyDeclined: 'Action has already been taken',
    TimedOut: 'Timed Out',
    LawyerAlreadyRejected: 'Action has already been taken',
    ActionMissing: 'Action Missing',
    NotFound: 'Not Found',
    NoSuchInstanceFound: 'Not Found',
    NewManualQuoteSent: 'Quote Expired',
    ClientAlreadyRequestedACall: 'Call requested has not been registered',
    Expired: 'Quote Expired',
    Failed: 'Something went wrong!',
    Terminated: 'Something went wrong!',
    WorkflowAlreadyCompleted: 'Action has already been taken',
} as const satisfies Record<ApprovalResponseStatus, string>;

export const ALL_CLIENT_ACCEPTANCE_STATUSES = [
    'Pending',
    'Declined',
    'Expired',
    'Accepted',
    'RequestACall',
] as const;
export type ClientAcceptanceStatus = (typeof ALL_CLIENT_ACCEPTANCE_STATUSES)[number];

export const ALL_CLIENT_APPROVAL_ACTIONS = ['Accepted', 'Declined', 'RequestCall'] as const;
export type ClientApprovalAction = (typeof ALL_CLIENT_APPROVAL_ACTIONS)[number];

export const ALL_LAWYER_APPROVAL_ACTIONS = ['Approved', 'Declined'] as const;
export type LawyerApprovalAction = (typeof ALL_LAWYER_APPROVAL_ACTIONS)[number];

export function parseLawyerApprovalAction(
    value: string | null | undefined,
): LawyerApprovalAction | null {
    if (value != null && ALL_LAWYER_APPROVAL_ACTIONS.includes(value as LawyerApprovalAction)) {
        return value as LawyerApprovalAction;
    }
    return null;
}

export function parseClientApprovalAction(
    value: string | null | undefined,
): ClientApprovalAction | null {
    if (value != null && ALL_CLIENT_APPROVAL_ACTIONS.includes(value as ClientApprovalAction)) {
        return value as ClientApprovalAction;
    }
    return null;
}

export const ALL_EMAIL_FREQUENCY_OF_REMINDERS = [
    'Daily',
    'EverySecondDay',
    'EveryThirdDay',
    'EveryFourthDay',
    'EveryFifthDay',
] as const;
export type EmailFrequencyOfReminder = (typeof ALL_EMAIL_FREQUENCY_OF_REMINDERS)[number];

export const EMAIL_FREQUENCY_OF_REMINDER_LABELS = {
    Daily: 'Daily',
    EverySecondDay: 'Every Second Day',
    EveryThirdDay: 'Every Third Day',
    EveryFourthDay: 'Every Fourth Day',
    EveryFifthDay: 'Every Fifth Day',
} as const satisfies Record<EmailFrequencyOfReminder, string>;

export const ALL_ACTIVITY_EMAIL_TYPES = [
    'ApprovalEmail',
    'LawyerDeclineEmail',
    'QuoteEmail',
    'QuoteEmailReminder',
    'IntakeEmail',
    'IntakeReminder',
    'QuoteEmailToLawyerForApproval',
    'QuoteApprovalToClient',
    'ClientDeclineEmail',
    'RequestACall',
    'PostIntakeEmail',
    'QuoteEmailReminderExhaustNotificationToLawyer',
    'IntakeEmailReminderExhaustNotificationToLawyer',
    'TreefortIdvCompletionMailToLawyer',
] as const;
export type ActivityEmailType = (typeof ALL_ACTIVITY_EMAIL_TYPES)[number];

export const ALL_EMAIL_TEMPLATE_TYPES = [
    'QuoteEmailTemplate',
    'QuoteEmailReminderTemplate',
    'IntakeEmailTemplate',
    'IntakeEmailReminderTemplate',
    'PostIntakeEmailTemplate',
] as const;
export type EmailTemplateType = (typeof ALL_EMAIL_TEMPLATE_TYPES)[number];

export const REAL_ESTATE_MATTER_TYPES = [
    'Sale',
    'Purchase',
    'Refinance',
    'PrivateLender',
    'TitleTransfer',
    'BareTransfer',
    'BareTransferAndRefinance',
    'ILA',
    'PurchaseAndSale',
    'Transfer',
    'TransferAndRefinance',
    'TransferOfTitle',
    'SurvivorshipApplication',
] as const;
export type RealEstateMatterType = (typeof REAL_ESTATE_MATTER_TYPES)[number];

export const INCORPORATION_MATTER_TYPES = [
    'ForProfitIncorporation',
    'NotForProfitIncorporation',
    'Corporate',
] as const;
export type IncorporationMatterType = (typeof INCORPORATION_MATTER_TYPES)[number];

export const WILLS_AND_ESTATES_MATTER_TYPES = [
    'EstatePlanning',
    'EstateAdministration',
    'PowerOfAttorney',
] as const;
export type WillsAndEstatesMatterType = (typeof WILLS_AND_ESTATES_MATTER_TYPES)[number];

export const LEXO_CIVIL_MATTER_TYPES = [
    'PersonalInjury',
    'EmploymentLaw',
    'LandlordTenant',
    'MotorVehicleAccident',
] as const;

export const CIVIL_MATTER_TYPES = ['Civil', ...LEXO_CIVIL_MATTER_TYPES, 'Litigation'] as const;
export type CivilMatterType = (typeof CIVIL_MATTER_TYPES)[number];

export const LEXO_FAMILY_MATTER_TYPES = [
    'Divorce',
    'MarriageCohabitation',
    'FertilityLaw',
    'MotionToChange',
    'IndependentLegalAdvice',
] as const;

export const RAMSAY_FAMILY_MATTER_TYPES = [
    'Divorce',
    'CohabitationAgreement',
    'SeparationAgreement',
    'ParentingAgreement',
    'SpousalSupport',
] as const;

export const FAMILY_MATTER_TYPES = [
    'Family',
    ...LEXO_FAMILY_MATTER_TYPES,
    'CohabitationAgreement',
    'SeparationAgreement',
    'ParentingAgreement',
    'SpousalSupport',
    'SeparationAndDivorce',
] as const;
export type FamilyMatterType = (typeof FAMILY_MATTER_TYPES)[number];

export const LEXO_CRIMINAL_MATTER_TYPES = ['ImpairedDriving', 'Assault', 'TheftAndFraud'] as const;

export const CRIMINAL_MATTER_TYPES = ['Criminal', ...LEXO_CRIMINAL_MATTER_TYPES] as const;
export type CriminalMatterType = (typeof CRIMINAL_MATTER_TYPES)[number];

export const LEXO_IMMIGRATION_MATTER_TYPES = [
    'TemporaryStatus',
    'PermanentResidence',
    'Citizenship',
] as const;

export const IMMIGRATION_MATTER_TYPES = ['Immigration', ...LEXO_IMMIGRATION_MATTER_TYPES] as const;
export type ImmigrationMatterType = (typeof IMMIGRATION_MATTER_TYPES)[number];

export const GENERAL_INTAKE_MATTER_TYPES = ['GeneralIntake'] as const;
export type GeneralIntakeMatterType = (typeof GENERAL_INTAKE_MATTER_TYPES)[number];

export const ALL_PROSPECT_MATTER_TYPES = [
    ...REAL_ESTATE_MATTER_TYPES,
    ...INCORPORATION_MATTER_TYPES,
    ...WILLS_AND_ESTATES_MATTER_TYPES,
    ...CIVIL_MATTER_TYPES,
    ...FAMILY_MATTER_TYPES,
    ...CRIMINAL_MATTER_TYPES,
    ...IMMIGRATION_MATTER_TYPES,
    ...GENERAL_INTAKE_MATTER_TYPES,
] as const;
export type ProspectMatterType = (typeof ALL_PROSPECT_MATTER_TYPES)[number];

export const ALL_PROSPECT_PRACTICE_AREAS = [
    'RealEstate',
    'Incorporation',
    'WillsAndEstates',
    'Civil',
    'Family',
    'Criminal',
    'Immigration',
    'GeneralIntake',
] as const;
export type ProspectPracticeArea = (typeof ALL_PROSPECT_PRACTICE_AREAS)[number];

export function isProspectPracticeArea(value: string): value is ProspectPracticeArea {
    return ALL_PROSPECT_PRACTICE_AREAS.includes(value as ProspectPracticeArea);
}

export const ALL_MATTER_TYPES = [...ALL_PROSPECT_MATTER_TYPES, 'StandAloneIDV'] as const;
export type MatterType = (typeof ALL_MATTER_TYPES)[number];

export const MATTER_TYPE_LABELS = {
    Sale: 'Sale',
    Purchase: 'Purchase',
    Refinance: 'Refinance',
    PrivateLender: 'Private Lender',
    TitleTransfer: 'Title Transfer',
    BareTransfer: 'Bare Transfer',
    BareTransferAndRefinance: 'Bare Transfer and Refinance',
    ILA: 'Independent Legal Advice',
    PurchaseAndSale: 'Purchase & Sale',
    Transfer: 'Transfer',
    TransferAndRefinance: 'Transfer & Refinance',
    TransferOfTitle: 'Transfer of Title',
    SurvivorshipApplication: 'Survivorship Application',
    ForProfitIncorporation: 'For-Profit Incorp',
    NotForProfitIncorporation: 'Not-For-Profit Incorp',
    Corporate: 'Corporate',
    EstatePlanning: 'Estate Planning',
    EstateAdministration: 'Estate Administration',
    PowerOfAttorney: 'Power of Attorney',
    Civil: 'Civil',
    PersonalInjury: 'Personal Injury',
    EmploymentLaw: 'Employment Law',
    LandlordTenant: 'Landlord & Tenant',
    MotorVehicleAccident: 'Motor Vehicle Accident',
    Litigation: 'Litigation',
    Family: 'Family',
    Divorce: 'Divorce',
    CohabitationAgreement: 'Cohabitation Agreement',
    SeparationAgreement: 'Separation Agreement',
    ParentingAgreement: 'Parenting Agreement',
    SpousalSupport: 'Spousal Support',
    MarriageCohabitation: 'Marriage/Cohabitation',
    FertilityLaw: 'Fertility Law',
    MotionToChange: 'Motion to Change',
    SeparationAndDivorce: 'Separation & Divorce',
    IndependentLegalAdvice: 'Independent Legal Advice',
    Criminal: 'Criminal',
    ImpairedDriving: 'Impaired Driving',
    Assault: 'Assault',
    TheftAndFraud: 'Theft & Fraud',
    Immigration: 'Immigration',
    TemporaryStatus: 'Temporary Status',
    PermanentResidence: 'Permanent Residence',
    Citizenship: 'Citizenship',
    GeneralIntake: 'General Intake',
    StandAloneIDV: 'Stand Alone IDV',
} as const satisfies Record<MatterType, string>;

export const ALL_PRACTICE_AREAS = [...ALL_PROSPECT_PRACTICE_AREAS, 'IDV'] as const;
export type PracticeArea = (typeof ALL_PRACTICE_AREAS)[number];

export const PRACTICE_AREA_LABELS = {
    RealEstate: 'Real Estate',
    Incorporation: 'Corporate',
    WillsAndEstates: 'Wills & Estates',
    Civil: 'Civil',
    Family: 'Family',
    Criminal: 'Criminal',
    Immigration: 'Immigration',
    GeneralIntake: 'General Intake',
    IDV: 'IDV',
} as const satisfies Record<PracticeArea, string>;

export const ALL_IDV_STATUSES = ['Request Pending', 'Review Report', 'Cancelled'] as const;
export type IdvStatus = (typeof ALL_IDV_STATUSES)[number];

export const ALL_PROVINCES = [
    'Ontario',
    'Alberta',
    'Nova Scotia',
    'Prince Edward Island',
    'New Brunswick',
    'British Columbia',
    'Saskatchewan',
    'Northwest Territories',
    'Nunavut',
    'Manitoba',
    'Yukon',
    'Newfoundland and Labrador',
    'Quebec',
] as const;
export type Province = (typeof ALL_PROVINCES)[number];

export const ALL_SALUTATIONS = ['Mr', 'Ms', 'Mrs', 'Dr', 'None'] as const;
export type Salutation = (typeof ALL_SALUTATIONS)[number];

/** QuoteIntake is V1-only; V2 practice areas use IntakeOnly. */
export const ALL_TEMPLATE_TYPES = ['QuoteIntake', 'IntakeOnly'] as const;
export type TemplateType = (typeof ALL_TEMPLATE_TYPES)[number];

export const ALL_OPERATOR_TYPES = ['lessThan', 'between', 'greaterThan'] as const;
export type OperatorType = (typeof ALL_OPERATOR_TYPES)[number];

export const ALL_PRICING_MODEL_TYPES = ['FlatFee', 'Threshold'] as const;
export type PricingModelType = (typeof ALL_PRICING_MODEL_TYPES)[number];

export const ALL_CONVEYANCERS = ['unity', 'goveyance'] as const;
export type Conveyancer = (typeof ALL_CONVEYANCERS)[number];

export const ALL_TEMPLATE_FORM_STATUSES = ['Draft', 'Completed'] as const;
export type TemplateFormStatus = (typeof ALL_TEMPLATE_FORM_STATUSES)[number];

export const ALL_TEMPLATE_FORM_TYPES = ['IntakeForm', 'QuoteForm'] as const;
export type TemplateFormType = (typeof ALL_TEMPLATE_FORM_TYPES)[number];
